import {
  useEffect,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";
import {
  GoogleMap,
  useLoadScript,
  Polygon,
  Circle,
  Marker,
} from "@react-google-maps/api";
import { FaLocationArrow, FaExpand, FaCompress } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { googleMapsKey } from "@/utils/helpers";
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';

interface DeliveryAreaSelectorProps {
  enabled: boolean;
  radius: number;
  setRadius: Dispatch<SetStateAction<number>>;
  selectedLocation: google.maps.LatLngLiteral;
  setSelectedLocation: Dispatch<SetStateAction<google.maps.LatLngLiteral>>;
  selectedPolygon: google.maps.LatLngLiteral[] | null;
  setSelectedPolygon: Dispatch<SetStateAction<google.maps.LatLngLiteral[] | null>>;
  locationUrl: string | null;
  setLocationUrl: Dispatch<SetStateAction<string | null>>;
}

const MAX_RADIUS = 50;
const MIN_RADIUS = 1;

const GOOGLE_MAPS_LIBRARIES: Array<"drawing"> = ["drawing"];

export default function DeliveryAreaSelector({
  enabled,
  radius,
  setRadius,
  selectedLocation,
  setSelectedLocation,
  selectedPolygon,
  setSelectedPolygon,
  setLocationUrl,
}: DeliveryAreaSelectorProps) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const isInitializedRef = useRef(false);
  // Set initial mode based on whether we have polygon data
  const [mode, setMode] = useState<"circle" | "polygon">(
    selectedPolygon && selectedPolygon.length > 0 ? "polygon" : "circle"
  );

  const [zoom, setZoom] = useState(12); // Default zoom level
  const [polygonPoints, setPolygonPoints] = useState<google.maps.LatLngLiteral[]>(selectedPolygon || []);
  const [mapType, setMapType] = useState<"roadmap" | "hybrid">("hybrid");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLocationSet, setIsLocationSet] = useState(false);

  const { t } = useTranslation();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: googleMapsKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Check if running on mobile platform
  const isMobile = Capacitor.isNativePlatform();

  // Update location URL whenever location changes
  useEffect(() => {
    if (selectedLocation?.lat && selectedLocation?.lng) {
      setLocationUrl(`https://www.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}`);
    }
  }, [selectedLocation, setLocationUrl]);

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !enabled || !isLoaded || isInitializedRef.current) return;
    isInitializedRef.current = true;

    // Initialize drawing manager
    if (google.maps.drawing && !drawingManagerRef.current) {
      const manager = new google.maps.drawing.DrawingManager({
        drawingMode: mode === "polygon" ? google.maps.drawing.OverlayType.POLYGON : null,
        drawingControl: false,
        polygonOptions: {
          fillColor: "#00A86B33",
          strokeColor: "#00A86B",
          strokeWeight: 2,
        },
      });
      drawingManagerRef.current = manager;
      manager.setMap(mapRef.current);

      google.maps.event.addListener(manager, "polygoncomplete", (polygon: google.maps.Polygon) => {
        const path = polygon.getPath().getArray().map((latLng) => ({
          lat: latLng.lat(),
          lng: latLng.lng(),
        }));
        setSelectedPolygon(path);
        setPolygonPoints(path);
        polygon.setMap(null);
      });
    }

    // Center map based on current data
    if (selectedPolygon && selectedPolygon.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      selectedPolygon.forEach((point) => bounds.extend(point));
      mapRef.current.fitBounds(bounds);
    } else if (selectedLocation.lat !== 0 && selectedLocation.lng !== 0) {
      mapRef.current.panTo(selectedLocation);
      mapRef.current.setZoom(zoom); // Use current zoom level
    }
  }, [enabled, isLoaded, mode, selectedLocation, selectedPolygon, setSelectedPolygon, zoom]);

  // Handle location initialization and updates
  useEffect(() => {
    if (!enabled || !isLoaded) return;

    // If we have a valid location from props, use that
    if (selectedLocation.lat !== 0 && selectedLocation.lng !== 0) {
      setIsLocationSet(true);
      if (mapRef.current) {
        mapRef.current.panTo(selectedLocation);
        mapRef.current.setZoom(zoom); // Use current zoom level
      }
      return;
    }

    // Otherwise try to get geolocation
    if (!isLocationSet) {
      if (isMobile) {
        // Use Capacitor Geolocation for mobile
        Geolocation.getCurrentPosition()
          .then((position) => {
            const coords = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setSelectedLocation(coords);
            setIsLocationSet(true);
            if (mapRef.current) {
              mapRef.current.panTo(coords);
              mapRef.current.setZoom(zoom); // Use current zoom level
            }
          })
          .catch((err) => {
            console.warn("Capacitor Geolocation failed:", err);
            const defaultLocation = { lat: 0, lng: 0 };
            setSelectedLocation(defaultLocation);
            setIsLocationSet(true);
          });
      } else if (navigator.geolocation) {
        // Use browser geolocation for web
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const coords = {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            };
            setSelectedLocation(coords);
            setIsLocationSet(true);
            if (mapRef.current) {
              mapRef.current.panTo(coords);
              mapRef.current.setZoom(zoom); // Use current zoom level
            }
          },
          () => {
            console.warn("Browser Geolocation failed or denied.");
            const defaultLocation = { lat: 0, lng: 0 };
            setSelectedLocation(defaultLocation);
            setIsLocationSet(true);
          }
        );
      }
    }
  }, [enabled, selectedLocation, setSelectedLocation, isLocationSet, isLoaded, zoom, isMobile]);

  const handleMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    initializeMap();
  }, [initializeMap]);

  const toggleFullscreen = useCallback(() => {
    const container = mapContainerRef.current;
    if (!container) return;

    if (isMobile) {
      // Mobile-specific fullscreen handling
      if (isFullscreen) {
        // Exit fullscreen
        document.body.style.overflow = 'auto';
        setIsFullscreen(false);
      } else {
        // Enter fullscreen
        document.body.style.overflow = 'hidden';
        setIsFullscreen(true);
      }
    } else {
      // Standard fullscreen API for browsers
      if (document.fullscreenElement) {
        document.exitFullscreen();
        setIsFullscreen(false);
      } else {
        container.requestFullscreen()
          .then(() => setIsFullscreen(true))
          .catch((err) => console.error("Fullscreen failed:", err));
      }
    }
  }, [isFullscreen, isMobile]);

  // Handle fullscreen change events for non-mobile devices
  useEffect(() => {
    if (isMobile) return;

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isMobile]);

  const handleLocationClick = useCallback(() => {
    if (isMobile) {
      // Use Capacitor Geolocation for mobile
      Geolocation.getCurrentPosition({ enableHighAccuracy: true,timeout: 10000 })
        .then((position) => {
          const coords = { 
            lat: position.coords.latitude, 
            lng: position.coords.longitude 
          };
          setSelectedLocation(coords);

          if (mapRef.current) {
            mapRef.current.panTo(coords);
            const zoomInLevel = 16;
            mapRef.current.setZoom(zoomInLevel);
            setZoom(zoomInLevel);
          }
        })
        .catch((err) => {
          console.error("Capacitor Geolocation error:", err);
          alert("Unable to retrieve your location.");
        });
    } else if (navigator.geolocation) {
      // Use browser geolocation for web
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = { 
            lat: pos.coords.latitude, 
            lng: pos.coords.longitude 
          };
          setSelectedLocation(coords);

          if (mapRef.current) {
            mapRef.current.panTo(coords);
            const zoomInLevel = 16;
            mapRef.current.setZoom(zoomInLevel);
            setZoom(zoomInLevel);
          }
        },
        (err) => {
          console.error("Browser Geolocation error:", err);
          alert("Unable to retrieve your location.");
        }
      );
    }
  }, [setSelectedLocation, isMobile]);

  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!enabled || !e.latLng) return;

    const newLocation = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setSelectedLocation(newLocation);

    // If in circle mode and we have a map reference, optionally zoom in a bit
    if (mode === "circle" && mapRef.current) {
      mapRef.current.panTo(newLocation);
      const zoomInLevel = Math.max(zoom, 14); // Don't zoom in too much if already zoomed
      mapRef.current.setZoom(zoomInLevel);
      setZoom(zoomInLevel);
    }
  }, [enabled, mode, zoom, setSelectedLocation]);

  const handleMapIdle = useCallback(() => {
    if (!mapRef.current) return;
    const currentZoom = mapRef.current.getZoom();
    if (currentZoom) {
      setZoom(currentZoom);
    }
    const center = mapRef.current.getCenter();
    if (center) {
      const newCenter = { lat: center.lat(), lng: center.lng() };
      setSelectedLocation(newCenter);
    }
  }, [setSelectedLocation]);

  const containerClass = `relative rounded overflow-hidden ${isFullscreen
    ? "fixed top-0 left-0 w-screen h-screen z-50"
    : "h-[350px] w-full"
    }`;

  if (!enabled || !isLoaded) return null;

  return (
    <div className="space-y-4">
      <div className={containerClass} ref={mapContainerRef}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={selectedLocation.lat !== 0 && selectedLocation.lng !== 0 ? selectedLocation : undefined}
          zoom={zoom}
          onLoad={handleMapLoad}
          onClick={handleMapClick}  // This handles all map clicks
          onIdle={handleMapIdle}
          options={{
            disableDefaultUI: true,
            clickableIcons: false,
            scrollwheel: false,
            mapTypeId: mapType,
            gestureHandling: isFullscreen ? 'greedy' : 'auto',
          }}
        >
          {mode === "circle" && (
            <Circle
              center={selectedLocation}
              radius={radius * 1000}
              options={{
                strokeColor: "#00A86B",
                fillColor: "#00A86B33",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                clickable: false,  // Ensure the circle doesn't intercept clicks
              }}
            />
          )}
          {mode === "polygon" && polygonPoints.length > 0 && (
            <Polygon
              path={polygonPoints}
              options={{
                fillColor: "#00A86B33",
                strokeColor: "#00A86B",
                strokeWeight: 3,
              }}
            />
          )}
          {(selectedLocation.lat !== 0 && selectedLocation.lng !== 0) && (
            <Marker
  position={selectedLocation}
  icon={{
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.3"/>
          </filter>
        </defs>
        <g filter="url(#shadow)">
          <path fill="#00A86B" d="M24 2C16.82 2 11 7.82 11 15c0 8.25 13 27 13 27s13-18.75 13-27c0-7.18-5.82-13-13-13zm0 18.5c-3.03 0-5.5-2.47-5.5-5.5S20.97 9.5 24 9.5s5.5 2.47 5.5 5.5-2.47 5.5-5.5 5.5z"/>
        </g>
      </svg>
    `)}`,
    scaledSize: new window.google.maps.Size(40, 40), // Fixed: Changed _scaledSize to scaledSize
    anchor: new window.google.maps.Point(20, 40),
  }}
/>
          )}
        </GoogleMap>

        {/* Controls */}
        <div className="absolute left-2 top-2 z-10 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              setMode("polygon");
              if (drawingManagerRef.current) {
                drawingManagerRef.current.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
              }
            }}
            className={`px-3 py-1 rounded text-sm font-semibold w-28 transition ${mode === "polygon" ? "bg-sidebar text-skyblue" : "bg-white text-black hover:bg-sidebar hover:text-skyblue"}`}
          >
            {t("delivery.polygonMode")}
          </button>
          <button
            type="button"
            onClick={() => {
              setMode("circle");
              if (drawingManagerRef.current) {
                drawingManagerRef.current.setDrawingMode(null);
              }
              setSelectedPolygon(null);
              setPolygonPoints([]);
            }}
            className={`px-3 py-1 rounded text-sm font-semibold w-28 transition ${mode === "circle" ? "bg-sidebar text-skyblue" : "bg-white text-black hover:bg-sidebar hover:text-skyblue"}`}
          >
            {t("delivery.circleMode")}
          </button>
        </div>

        {/* Zoom + MapType + Fullscreen */}
        <div className="absolute right-2 top-2 z-10 flex flex-col gap-2">
          <button type="button" onClick={handleLocationClick} className="bg-white rounded shadow w-10 h-10 flex items-center justify-center hover:bg-gray-100"><FaLocationArrow /></button>
          <button
            type="button"
            onClick={() => {
              if (mapRef.current) {
                const newZoom = Math.min(mapRef.current.getZoom()! + 1, 21);
                mapRef.current.setZoom(newZoom);
                setZoom(newZoom);
              }
            }}
            className="bg-white rounded shadow w-10 h-10 flex items-center justify-center hover:bg-gray-100">+
          </button>
          <button
            type="button"
            onClick={() => {
              if (mapRef.current) {
                const newZoom = Math.max(mapRef.current.getZoom()! - 1, 3);
                mapRef.current.setZoom(newZoom);
                setZoom(newZoom);
              }
            }}
            className="bg-white rounded shadow w-10 h-10 flex items-center justify-center hover:bg-gray-100">−
          </button>
          <button
            type="button"
            onClick={() => setMapType("roadmap")}
            className={`bg-white rounded shadow p-1 h-10 w-10 hover:bg-gray-100 ${mapType === "roadmap" && "border-[3px] border-blue-500"}`}
          >
            <img
              src="/images/roadmap.png"
              alt="Roadmap"
              className="w-full h-full object-cover rounded"
            />
          </button>

          <button
            type="button"
            onClick={() => setMapType("hybrid")}
            className={`bg-white rounded shadow p-1 h-10 w-10 hover:bg-gray-100 ${mapType === "hybrid" && "border-[3px] border-blue-500"}`}
          >
            <img
              src="/images/satellite_720.png"
              alt="Hybrid"
              className="w-full h-full object-cover rounded"
            />
          </button>
          <button type="button" onClick={toggleFullscreen} className="bg-white rounded shadow w-10 h-10 flex items-center justify-center hover:bg-gray-100">
            {isFullscreen ? <FaCompress /> : <FaExpand />}
          </button>
        </div>
      </div>

      {/* Radius */}
      {mode === "circle" && (
        <div className="flex items-center gap-2 flex-wrap px-1">
          <label className="text-sm font-medium">{t("delivery.radius")}</label>
          <div className="flex items-center gap-1">
            <div onClick={() => setRadius(Math.max(radius - 1, MIN_RADIUS))} className="w-8 h-8 text-xl flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300 active:scale-95 transition">−</div>
            <input
              type="number"
              value={radius}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (!isNaN(val)) setRadius(Math.min(Math.max(val, MIN_RADIUS), MAX_RADIUS));
              }}
              className="w-20 border rounded text-center px-2 py-1 font-semibold focus:ring focus:ring-green-300"
            />
            <div onClick={() => setRadius(Math.min(radius + 1, MAX_RADIUS))} className="w-8 h-8 text-xl flex items-center justify-center bg-gray-200 rounded hover:bg-gray-300 active:scale-95 transition">+</div>
          </div>
        </div>
      )}

      {/* Clear Polygon */}
      {mode === "polygon" && polygonPoints.length > 0 && (
        <button
          onClick={() => {
            setSelectedPolygon(null);
            setPolygonPoints([]);
          }}
          className="px-4 py-1 bg-red-500 text-white rounded hover:bg-red-600"
        >
          {t("delivery.clearPolygon")}
        </button>
      )}
    </div>
  );
}